import type { AffiliationTypes } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { Box, Typography } from 'hephaestus/web';
import { groupBy } from 'lodash-es';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountConfigSection } from '../accountConfig';
import { useFetchAccount } from '../editAccount.fetchAccount';
import type { AffiliateModalProps } from './affiliateModal.types';

type UseRelatedAffiliateSelectorProps = {
  isDisabled?: boolean;
  account?: Partial<Account>;
  title: string;
  relatedAffiliationType: AffiliationTypes;
  targetAffiliationType: AffiliationTypes;
  'data-testid': string;
  show?: boolean;
  affiliate?: Affiliation;
  affiliateModalProps: AffiliateModalProps;
  affiliateSections: AccountConfigSection[];
  showMissingError?: boolean;
};

type RelatedAffiliationSelectorState = Partial<Affiliation> | 'SUBSCRIBER';
type UseRelatedAffiliateSelectorReturn = {
  selectedAffiliation?: RelatedAffiliationSelectorState;
  jsx: JSX.Element | null;
  type: AffiliationTypes;
};

export const createUseRelatedAffiliationSelector = (
  AffiliateModalComponent: ComponentType<AffiliateModalProps>,
) => {
  const useRelatedAffiliationSelector = (
    props: UseRelatedAffiliateSelectorProps,
  ): UseRelatedAffiliateSelectorReturn => {
    const {
      isDisabled = false,
      account,
      title,
      relatedAffiliationType,
      targetAffiliationType,
      show = true,
      affiliate,
      affiliateModalProps,
      affiliateSections,
      showMissingError,
    } = props;
    const accountConfonfigSection = (affiliateSections || []).find(
      (s) => s.affiliateType === relatedAffiliationType,
    );

    const initialRelatedAffiliate = affiliate?.relatedAffiliations
      ?.map((id) => account?.affiliations?.find((a) => a?.id === id))
      ?.find((affiliation) => affiliation?.type === relatedAffiliationType);
    const [newAffiliateModalOpen, setNewAffiliateModalOpen] = useState(false);
    const [selectedAffiliation, setSelectedAffiliation] = useState<
      RelatedAffiliationSelectorState | undefined
    >(initialRelatedAffiliate);
    const isSubscriberRelatedAffiliate =
      selectedAffiliation === 'SUBSCRIBER' ||
      selectedAffiliation?.user?.id === account?.user?.id;

    useEffect(() => {
      if (show) setSelectedAffiliation(initialRelatedAffiliate);
    }, [initialRelatedAffiliate?.id, show]);

    const affiliationsMatchingType = account?.affiliations?.filter(
      (a) => a.type === relatedAffiliationType,
    );
    const affiliateOptions = affiliationsMatchingType?.filter(
      (a) => a?.user?.id !== account?.user?.id,
    );
    const subscriberAffiliation = affiliationsMatchingType?.find(
      (a) => a?.user?.id === account?.user?.id,
    );

    const { t } = useTranslation(['affiliationTypes', 'client']);

    const affiliationLabel = t(
      `affiliationTypes:${relatedAffiliationType}`,
    ).toLowerCase();
    const targetLabel = t(
      `affiliationTypes:${targetAffiliationType}`,
    ).toLowerCase();

    const onAffiliateModalSubmitted: AffiliateModalProps['onAffiliationsUpdated'] =
      (args) => {
        const { type, updateData, userId } = args;
        affiliateModalProps.onAffiliationsUpdated?.(args);

        if (!updateData || !userId) return;
        const updatedAffiliation =
          updateData.updateAffiliations.account.affiliations?.find(
            (a) => a.user?.id === userId && a.type === type,
          );

        setTimeout(() => {
          setSelectedAffiliation(updatedAffiliation);
        }, 0);
      };

    const [fetchAccount, { data: backendAccount }] = useFetchAccount(
      affiliateModalProps.account.id,
    );
    useEffect(() => {
      if (show) fetchAccount();
    }, [show]);
    const existingAffiliationsById = groupBy(
      backendAccount?.fetchAccount?.account?.affiliations,
      (aff) => aff.id,
    );

    const jsx = show ? (
      <Box
        sx={{
          mt: 3,
        }}
      >
        <Typography sx={{ mb: 1 }} variant='titleMedium'>
          {title}
        </Typography>
        <CheckboxField
          disabled={isDisabled}
          data-testid={props['data-testid']}
          error={showMissingError && !selectedAffiliation}
          checked={isSubscriberRelatedAffiliate}
          label={t('affiliationTypes:accountHolderRelationshipLabel', {
            accountHolderFirstName: account?.user?.firstName,
            accountHolderLastName: account?.user?.lastName,
            affiliationType: affiliationLabel,
            targetType: targetLabel,
          })}
          onChange={(checked: boolean) =>
            setSelectedAffiliation(
              checked ? subscriberAffiliation || 'SUBSCRIBER' : undefined,
            )
          }
        />
        {!isSubscriberRelatedAffiliate && (
          <Box
            sx={{
              mt: 1,
            }}
          >
            <SelectField
              testId={`related-affiliate-select-${relatedAffiliationType}`}
              fullWidth
              label={t('affiliationTypes:selectAlternativeLabel', {
                affiliationType: affiliationLabel,
              })}
              error={showMissingError && !selectedAffiliation}
              value={selectedAffiliation?.id || null}
              onChange={(e: { target: { value: string } }) =>
                setSelectedAffiliation(
                  affiliateOptions?.find((a) => a?.id === e.target.value),
                )
              }
            >
              {affiliateOptions?.map((a) => (
                <MenuItem
                  key={a.id}
                  value={a.id}
                  data-testid={`related-affiliate-select-${relatedAffiliationType}-${a.id}`}
                >
                  {a.user.firstName} {a.user.lastName}
                </MenuItem>
              ))}
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setNewAffiliateModalOpen(true);
                }}
              >
                <Typography color='primary' variant='bodyLarge'>
                  {t('affiliationTypes:addNewLabel', {
                    affiliationType: affiliationLabel,
                  })}
                </Typography>
              </MenuItem>
            </SelectField>
          </Box>
        )}
        {account && account.affiliations && (
          <AffiliateModalComponent
            {...affiliateModalProps}
            account={{
              ...affiliateModalProps.account,
              affiliations: affiliateModalProps.account.affiliations?.map(
                (aff) =>
                  existingAffiliationsById[aff.id as string]?.at(0) || aff,
              ),
            }}
            affiliates={affiliateModalProps.affiliates?.map(
              (aff) => existingAffiliationsById[aff.id as string]?.at(0) || aff,
            )}
            allAffiliates={affiliateModalProps.allAffiliates?.map((aff) =>
              'id' in aff
                ? existingAffiliationsById[aff.id as string]?.at(0) || aff
                : aff,
            )}
            onAffiliationsUpdated={onAffiliateModalSubmitted}
            type={relatedAffiliationType}
            action='create'
            open={newAffiliateModalOpen}
            setOpen={setNewAffiliateModalOpen}
            affiliate={{ user: {} } as any}
            fields={accountConfonfigSection?.fields}
            optionalFields={accountConfonfigSection?.optionalFields}
          />
        )}
      </Box>
    ) : null;

    return { selectedAffiliation, jsx, type: relatedAffiliationType };
  };

  return useRelatedAffiliationSelector;
};
